import { Input } from '@chakra-ui/input';
import { Divider, Heading, SimpleGrid } from '@chakra-ui/layout';
import {
  Button,
  chakra,
  FormControl,
  FormHelperText,
  FormLabel,
  Icon,
  IconButton,
  InputGroup,
  InputRightElement,
  Popover,
  PopoverContent,
  PopoverTrigger,
  useToast,
} from '@chakra-ui/react';
import firebase from 'firebase';
import React, { useEffect, useState } from 'react';
import { HexColorPicker } from 'react-colorful';
import { PenTool } from 'react-feather';
import { Controller, useForm } from 'react-hook-form';
import { useConfig } from '../../../hooks/useConfig';
import { FirebaseConfig } from '../../../types/types';
import { AdminConfigStandardImageModal } from './AdminConfigStandardImageModal';

const Form = chakra('form');

interface AdminConfigTabProps {}

export const AdminConfigTab: React.FC<AdminConfigTabProps> = () => {
  const form = useForm<FirebaseConfig>();
  const toast = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const firebaseConfig = useConfig();

  useEffect(() => {
    if (firebaseConfig) {
      form.reset(firebaseConfig);
    }
  }, [firebaseConfig]);

  const onSubmit = form.handleSubmit(async (formValues) => {
    setIsSubmitting(true);
    try {
      await firebase
        .firestore()
        .collection('config')
        .doc('default')
        .update(formValues);
      setIsSubmitting(false);
      toast({ title: 'Erfolgreich gespeichert', status: 'success' });
    } catch (e) {
      setIsSubmitting(false);
      try {
        await firebase
          .firestore()
          .collection('errors')
          .add({ type: 'config', error: JSON.stringify(e) });
      } catch (err) {
        console.error(err);
      }
      toast({ title: 'Fehler beim Speichern', status: 'error' });
    }
  });

  return (
    <>
      <Heading size="md" mb={6}>
        Einstellungen
      </Heading>
      <Heading size="sm" mb={4}>
        1. Allgemein
      </Heading>
      <Form onSubmit={onSubmit}>
        <FormControl isRequired={true} mb={6}>
          <FormLabel>Studioname</FormLabel>
          <Input
            focusBorderColor="brand.500"
            {...form.register('displayName', { required: true })}
          />
        </FormControl>
        <FormControl isRequired={true} mb={6}>
          <FormLabel>Kontakt-Url</FormLabel>
          <Input
            focusBorderColor="brand.500"
            {...form.register('contactUrl', { required: true })}
          />
        </FormControl>
        <SimpleGrid columns={[1, 1, 2]} gap={4}>
          <FormControl isRequired={true} mb={6}>
            <FormLabel>Impressum-Url</FormLabel>
            <Input
              focusBorderColor="brand.500"
              {...form.register('imprintUrl', { required: true })}
            />
          </FormControl>
          <FormControl isRequired={true} mb={6}>
            <FormLabel>Datenschutz-Url</FormLabel>
            <Input
              focusBorderColor="brand.500"
              {...form.register('privacyUrl', { required: true })}
            />
          </FormControl>
          <FormControl isRequired={true} mb={6}>
            <FormLabel>Farbschema</FormLabel>
            <InputGroup>
              <InputRightElement>
                <Popover matchWidth={true} placement="auto">
                  <PopoverTrigger>
                    <IconButton
                      size="sm"
                      aria-label="edit"
                      icon={<Icon as={PenTool} />}
                    />
                  </PopoverTrigger>
                  <PopoverContent w="auto" border="none" bg="transparent">
                    <Controller
                      control={form.control}
                      name="color"
                      render={({ field }) => (
                        <HexColorPicker
                          color={form.watch().color}
                          onChange={field.onChange}
                        />
                      )}
                    />
                  </PopoverContent>
                </Popover>
              </InputRightElement>
              <Input
                name="color"
                focusBorderColor="brand.500"
                onChange={(e) => form.setValue('color', e.target.value)}
                defaultValue="#ffffff"
                value={form.watch().color}
              />
            </InputGroup>
          </FormControl>
        </SimpleGrid>
        <Divider mb={6} />
        <Heading size="sm" mb={4}>
          2. Kurseinstellungen
        </Heading>
        <SimpleGrid columns={[1, 1, 2]} gap={4}>
          <FormControl isRequired={true} mb={6}>
            <FormLabel>Virtueller Kursraum offen vor Start (in min.)</FormLabel>
            <Input
              pattern="\d*"
              step=".01"
              onWheel={(e) => e.currentTarget.blur()}
              type="number"
              focusBorderColor="brand.500"
              {...form.register('courseJoinBeforeDuration', {
                required: true,
                valueAsNumber: true,
              })}
            />
            <FormHelperText>
              In dieser Zeit vor Kursantritt können die Mitglieder bereits in
              den Kurs eintreten
            </FormHelperText>
          </FormControl>
          <FormControl isRequired={true} mb={6}>
            <FormLabel>Registrierungen X Tage vorher möglich</FormLabel>
            <Input
              pattern="\d*"
              step=".01"
              onWheel={(e) => e.currentTarget.blur()}
              type="number"
              focusBorderColor="brand.500"
              {...form.register('courseAllowedRegisterDays', {
                required: true,
                valueAsNumber: true,
              })}
            />
            <FormHelperText>
              Die Anzahl an Tage, in welcher bereits eine Reservierung für den
              Kurs mögliuch ist
            </FormHelperText>
          </FormControl>
        </SimpleGrid>
        <Divider mb={6} />
        <Heading size="sm" mb={4}>
          3. Marketing
        </Heading>
        <SimpleGrid columns={[1, 1, 2]} gap={4}>
          <FormControl isRequired={true} mb={6}>
            <FormLabel>Ablauf Testversion (in Tagen)</FormLabel>
            <Input
              pattern="\d*"
              step=".01"
              onWheel={(e) => e.currentTarget.blur()}
              type="number"
              focusBorderColor="brand.500"
              {...form.register('trialExpiration', {
                required: true,
                valueAsNumber: true,
              })}
            />
          </FormControl>
          <FormControl isRequired={true} mb={6}>
            <FormLabel>Gutschein für Online Mitgliedschaft (in Euro)</FormLabel>
            <Input
              pattern="\d*"
              step=".01"
              onWheel={(e) => e.currentTarget.blur()}
              type="number"
              focusBorderColor="brand.500"
              {...form.register('thankYouVoucher', {
                required: true,
                valueAsNumber: true,
              })}
            />
            <FormHelperText>
              Hier kann ein Gutscheinwert angegeben werden. Fall es keinen
              Gutschein gibt, bitte eine 0 eintragen.
            </FormHelperText>
          </FormControl>
        </SimpleGrid>
        <SimpleGrid columns={[1, 1, 2]} gap={4}>
          <FormControl isRequired={true} mb={6}>
            <FormLabel>Preis der Online-Mitgliedschaft</FormLabel>
            <Input
              pattern="\d*"
              step=".01"
              onWheel={(e) => e.currentTarget.blur()}
              type="number"
              focusBorderColor="brand.500"
              {...form.register('onlineMembershipFee', {
                required: true,
                valueAsNumber: true,
              })}
            />
          </FormControl>
          <FormControl isRequired={true} mb={6}>
            <FormLabel>Link zur Online-Mitgliedschaft</FormLabel>
            <Input
              focusBorderColor="brand.500"
              {...form.register('onlineMembershipLink', { required: true })}
            />
          </FormControl>
        </SimpleGrid>
        <SimpleGrid columns={[1, 1, 2]} gap={4}>
          <FormControl isRequired={true} mb={6}>
            <FormLabel>Facebook-Pixel</FormLabel>
            <Input
              onWheel={(e) => e.currentTarget.blur()}
              focusBorderColor="brand.500"
              {...form.register('pixelId', {
                required: true,
              })}
            />
          </FormControl>
          <FormControl isRequired={true} mb={6}>
            <FormLabel>Landing Page - YouTube Video</FormLabel>
            <Input
              onWheel={(e) => e.currentTarget.blur()}
              focusBorderColor="brand.500"
              {...form.register('landingYouTubeVideo', {
                required: true,
              })}
            />
          </FormControl>
        </SimpleGrid>

        <Button
          isFullWidth={true}
          borderRadius="lg"
          type="submit"
          isLoading={isSubmitting}
        >
          Speichern
        </Button>
      </Form>
      <Divider my={6} />
      <Heading size="sm" mb={4}>
        4. Bilder
      </Heading>
      <AdminConfigStandardImageModal
        cta="Standard-Kursbild bearbeiten"
        title="Standard-Kursbild"
        description="Dieses Bild wird immer dann angezeigt, wenn kein anderes ausgewählt worden ist."
        firebaseRef={firebase.firestore().collection('config').doc('default')}
        configKey="fallbackImages"
        storageKey="fallback"
        previewImageRef={
          firebaseConfig?.fallbackImages?.cover
            ? firebaseConfig?.fallbackImages?.cover
            : ''
        }
      />
      <Divider my={4} />
      <AdminConfigStandardImageModal
        cta="Standard-Introbild bearbeiten"
        title="Standard-Introbild"
        description="Dieses Bild wird immer dann angezeigt, wenn kein anderes ausgewählt worden ist."
        firebaseRef={firebase.firestore().collection('config').doc('default')}
        configKey="hero"
        storageKey="hero"
        previewImageRef={
          firebaseConfig?.hero?.cover ? firebaseConfig?.hero?.cover : ''
        }
      />

      <Divider my={4} />
      <AdminConfigStandardImageModal
        cta="Logo bearbeiten"
        title="Logo"
        description="Logo für die Anwendung"
        firebaseRef={firebase.firestore().collection('config').doc('default')}
        configKey="logo"
        storageKey="logo"
        previewImageRef={
          firebaseConfig?.logo?.cover ? firebaseConfig?.logo?.cover : ''
        }
      />
      <Divider my={4} />
      <AdminConfigStandardImageModal
        cta="Landing-Page Header bearbeiten"
        title="Landing-Page Header"
        description="Header-Image für die Landing Page"
        firebaseRef={firebase.firestore().collection('config').doc('default')}
        configKey="landingHeader"
        storageKey="landingHeader"
        previewImageRef={
          firebaseConfig?.landingHeader?.cover
            ? firebaseConfig?.landingHeader?.cover
            : ''
        }
      />
    </>
  );
};
